/**
 * NIP-5A nsite hostname parsing and URL construction utilities.
 *
 * Handles three nsite URL formats:
 *   - Root site:  `<npub>.gateway.tld`
 *   - Named site: `<pubkeyB36><dTag>.gateway.tld`
 *   - Snapshot:   `v<snapshotIdB36>.gateway.tld`
 */

// ---------------------------------------------------------------------------
// Base-36 codec for 32-byte (256-bit) values
// ---------------------------------------------------------------------------

const BASE36_ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";

/** Encode a hex string (64 chars = 32 bytes) to base-36. Always pads to 50 chars. */
export function hexToBase36(hex: string): string {
  // Convert hex → bigint, then repeatedly divmod by 36.
  let n = BigInt("0x" + hex);
  if (n === 0n) return "0".repeat(50);
  const digits: string[] = [];
  while (n > 0n) {
    digits.push(BASE36_ALPHABET[Number(n % 36n)]);
    n /= 36n;
  }
  // Pad to 50 chars (32 bytes always encodes to exactly 50 base-36 digits).
  while (digits.length < 50) digits.push("0");
  return digits.reverse().join("");
}

/** Decode a 50-char base-36 string back to a 64-char lowercase hex string. */
export function base36ToHex(b36: string): string {
  let n = 0n;
  for (const ch of b36) {
    const idx = BASE36_ALPHABET.indexOf(ch);
    if (idx === -1) throw new Error(`Invalid base36 character: ${ch}`);
    n = n * 36n + BigInt(idx);
  }
  return n.toString(16).padStart(64, "0");
}

// ---------------------------------------------------------------------------
// Bech32 npub decoder (minimal, just for npub1… → hex pubkey)
// ---------------------------------------------------------------------------

const BECH32_ALPHABET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";

function bech32Decode(bech: string): { prefix: string; data: number[] } | null {
  const lower = bech.toLowerCase();
  const sepIdx = lower.lastIndexOf("1");
  if (sepIdx < 1) return null;
  const prefix = lower.slice(0, sepIdx);
  const dataChars = lower.slice(sepIdx + 1);
  const data: number[] = [];
  for (const ch of dataChars) {
    const idx = BECH32_ALPHABET.indexOf(ch);
    if (idx === -1) return null;
    data.push(idx);
  }
  // Strip 6-char checksum
  return { prefix, data: data.slice(0, -6) };
}

function convertBits(data: number[], fromBits: number, toBits: number, pad: boolean): number[] | null {
  let acc = 0;
  let bits = 0;
  const ret: number[] = [];
  const maxv = (1 << toBits) - 1;
  for (const value of data) {
    if (value < 0 || value >> fromBits !== 0) return null;
    acc = (acc << fromBits) | value;
    bits += fromBits;
    while (bits >= toBits) {
      bits -= toBits;
      ret.push((acc >> bits) & maxv);
    }
  }
  if (pad) {
    if (bits > 0) ret.push((acc << (toBits - bits)) & maxv);
  } else if (bits >= fromBits || ((acc << (toBits - bits)) & maxv) !== 0) {
    return null;
  }
  return ret;
}

/** Decode an npub1… string to a 64-char hex pubkey. Returns null on failure. */
export function decodeNpub(npub: string): string | null {
  const decoded = bech32Decode(npub);
  if (!decoded || decoded.prefix !== "npub") return null;
  const bytes = convertBits(decoded.data, 5, 8, false);
  if (!bytes || bytes.length !== 32) return null;
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ---------------------------------------------------------------------------
// Nsite context — parsed from the current hostname
// ---------------------------------------------------------------------------

export type NsiteContext =
  | { type: "root"; pubkey: string; gateway: string }
  | { type: "named"; pubkey: string; dTag: string; gateway: string }
  | { type: "snapshot"; snapshotId: string; gateway: string }
  | null;

/**
 * Parse the current page's hostname to extract nsite context.
 *
 * @param hostname  e.g. `window.location.hostname`
 * @returns         parsed context or `null` if not running on an nsite gateway
 */
export function parseNsiteHostname(hostname: string): NsiteContext {
  // Split into labels — we only care about the leftmost one.
  const labels = hostname.split(".");
  if (labels.length < 2) return null; // e.g. "localhost" — not an nsite
  const label = labels[0];
  const gateway = labels.slice(1).join(".");

  // 1. npub check — `npub1…`
  if (label.startsWith("npub1")) {
    const hex = decodeNpub(label);
    if (hex) return { type: "root", pubkey: hex, gateway };
  }

  // 2. Snapshot — `v` + 50 base36 chars
  if (/^v[0-9a-z]{50}$/.test(label)) {
    const snapshotIdB36 = label.slice(1);
    const snapshotId = base36ToHex(snapshotIdB36);
    return { type: "snapshot", snapshotId, gateway };
  }

  // 3. Named site — 50 base36 chars (pubkey) + 1-13 char d-tag
  if (/^[0-9a-z]{51,63}$/.test(label) && !label.endsWith("-")) {
    const pubkeyB36 = label.slice(0, 50);
    const dTag = label.slice(50);
    if (/^[a-z0-9-]{1,13}$/.test(dTag)) {
      const pubkey = base36ToHex(pubkeyB36);
      return { type: "named", pubkey, dTag, gateway };
    }
  }

  return null;
}

/**
 * Build a snapshot URL for a given nsite gateway.
 *
 * @param snapshotEventId  64-char hex event id
 * @param gateway          gateway domain, e.g. `nsite.lol`
 * @param protocol         `https:` or `http:`
 */
export function buildSnapshotUrl(
  snapshotEventId: string,
  gateway: string,
  protocol = "https:",
): string {
  const b36 = hexToBase36(snapshotEventId);
  return `${protocol}//v${b36}.${gateway}`;
}

/**
 * Build a named-site URL: `<pubkeyB36><dTag>.gateway`
 */
export function buildNamedSiteUrl(
  pubkeyHex: string,
  dTag: string,
  gateway: string,
  protocol = "https:",
): string {
  const b36 = hexToBase36(pubkeyHex);
  return `${protocol}//${b36}${dTag}.${gateway}`;
}

/**
 * Build a root-site URL: `<npub>.gateway`
 *
 * We use the base36 pubkey form here since building a valid npub requires
 * bech32 *encoding* which we haven't implemented. The nsite spec also
 * accepts the named-site format with an empty d-tag conceptually, but the
 * canonical root-site form is npub. For simplicity we just store the full
 * hostname from the `a` tag parse instead.
 */
export function buildLatestSiteUrl(
  aTagValue: string,
  gateway: string,
  protocol = "https:",
): string | null {
  // `a` tag format: `<kind>:<pubkey>:<d-tag>`
  const parts = aTagValue.split(":");
  if (parts.length < 3) return null;
  const [kindStr, pubkey, dTag] = parts;
  const kind = Number(kindStr);
  if (kind === 35128 && dTag) {
    return buildNamedSiteUrl(pubkey, dTag, gateway, protocol);
  }
  // For root sites (15128), build with just the base36 pubkey.
  // The spec says npub is canonical, but base36-only subdomains that are
  // exactly 50 chars don't match any nsite pattern so we'd need a real npub.
  // For now return null — root-site snapshots are an edge case we can handle later.
  if (kind === 15128) {
    // We can't easily produce an npub without bech32 encoding.
    // Return null and let the caller handle it.
    return null;
  }
  return null;
}

/** The default public nsite gateway to use as a fallback. */
export const DEFAULT_NSITE_GATEWAY = "nsite.lol";

// ---------------------------------------------------------------------------
// Hardcoded fallback for this specific site
// ---------------------------------------------------------------------------

/**
 * The known nsite subdomain for this app. When running outside an nsite
 * gateway (e.g. localhost, shakespeare.to) we use this to derive the pubkey,
 * d-tag, and `a`-tag filter so the version switcher always works.
 */
export const FALLBACK_NSITE_SUBDOMAIN = "0ygl461f4ecz5thkn8eksa36nepjpij86s83ti8yzokopd3ag5zap-galaxy";

/** Parse the hardcoded fallback subdomain into an NsiteContext. */
export function getFallbackNsiteContext(): NsiteContext {
  const label = FALLBACK_NSITE_SUBDOMAIN;
  const pubkeyB36 = label.slice(0, 50);
  const dTag = label.slice(50);
  const pubkey = base36ToHex(pubkeyB36);
  return { type: "named", pubkey, dTag, gateway: DEFAULT_NSITE_GATEWAY };
}
